import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RealTimeTaskUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [filter, setFilter] = useState('all'); // all, completed, in-progress, alerts

  const mockUpdates = [
  {
    id: 1,
    type: 'completion',
    member: "Sarah Johnson",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10df5a971-1765003957966.png",
    avatarAlt: "Professional woman with brown hair in white shirt smiling at camera",
    task: "Pest Inspection - Field A",
    location: { lat: 40.7128, lng: -74.0060, name: "Field A - North Section" },
    timestamp: new Date(Date.now() - 300000), // 5 minutes ago
    status: 'completed',
    details: "Inspection completed successfully. No major pest activity detected.",
    gpsVerified: true,
    photos: 3,
    priority: 'medium'
  },
  {
    id: 2,
    type: 'progress',
    member: "Mike Rodriguez",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13539cf1a-1763294982283.png",
    avatarAlt: "Hispanic man with beard wearing blue work shirt in outdoor setting",
    task: "Soil Testing - Field C",
    location: { lat: 40.7589, lng: -73.9851, name: "Field C - South Section" },
    timestamp: new Date(Date.now() - 120000), // 2 minutes ago
    status: 'in-progress',
    details: "50% complete. Collecting samples from grid points 1-8.",
    gpsVerified: true,
    photos: 1,
    priority: 'high'
  },
  {
    id: 3,
    type: 'alert',
    member: "Emily Chen",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5f852ff-1763293714178.png",
    avatarAlt: "Asian woman with long black hair in professional attire smiling outdoors",
    task: "Quality Assessment - Field D",
    location: { lat: 40.7831, lng: -73.9712, name: "Field D - West Section" },
    timestamp: new Date(Date.now() - 60000), // 1 minute ago
    status: 'alert',
    details: "Potential issue detected. Requesting supervisor review.",
    gpsVerified: true,
    photos: 2,
    priority: 'high'
  },
  {
    id: 4,
    type: 'start',
    member: "David Thompson",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f72ed58b-1763295933670.png",
    avatarAlt: "Caucasian man with brown hair in casual shirt outdoors with natural lighting",
    task: "Equipment Calibration",
    location: { lat: 40.7505, lng: -73.9934, name: "Equipment Shed" },
    timestamp: new Date(Date.now() - 900000), // 15 minutes ago
    status: 'started',
    details: "Beginning equipment calibration routine.",
    gpsVerified: true,
    photos: 0,
    priority: 'low'
  },
  {
    id: 5,
    type: 'delay',
    member: "Lisa Park",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_159b5443a-1763295748481.png",
    avatarAlt: "Asian woman with shoulder-length black hair in professional blazer smiling",
    task: "Data Collection - Field B",
    location: { lat: 40.7282, lng: -74.0776, name: "Field B - East Section" },
    timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
    status: 'delayed',
    details: "Task delayed due to equipment malfunction. ETA updated to 2:30 PM.",
    gpsVerified: false,
    photos: 0,
    priority: 'medium'
  }];


  useEffect(() => {
    setUpdates(mockUpdates);

    // Simulate real-time updates
    const interval = setInterval(() => {
      const newUpdate = {
        id: Date.now(),
        type: 'progress',
        member: "System Update",
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_193fbaec9-1778924893327.png",
        avatarAlt: "System generated update notification icon",
        task: "Automated Status Check",
        location: { lat: 40.7128, lng: -74.0060, name: "Multiple Locations" },
        timestamp: new Date(),
        status: 'system',
        details: "Automated system check completed. All teams operational.",
        gpsVerified: true,
        photos: 0,
        priority: 'low'
      };

      setUpdates((prev) => [newUpdate, ...prev?.slice(0, 9)]);
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':return { icon: 'CheckCircle', color: 'text-success' };
      case 'in-progress':return { icon: 'Clock', color: 'text-warning' };
      case 'alert':return { icon: 'AlertTriangle', color: 'text-error' };
      case 'started':return { icon: 'Play', color: 'text-primary' };
      case 'delayed':return { icon: 'AlertCircle', color: 'text-error' };
      case 'system':return { icon: 'Settings', color: 'text-muted-foreground' };
      default:return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':return 'border-l-error';
      case 'medium':return 'border-l-warning';
      case 'low':return 'border-l-success';
      default:return 'border-l-muted';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return timestamp?.toLocaleDateString();
  };

  const filteredUpdates = updates?.filter((update) => {
    if (filter === 'all') return true;
    if (filter === 'completed') return update?.status === 'completed';
    if (filter === 'in-progress') return update?.status === 'in-progress';
    if (filter === 'alerts') return update?.status === 'alert' || update?.status === 'delayed';
    return true;
  });

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
            <h3 className="text-lg font-semibold text-foreground">Real-Time Task Updates</h3>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="px-3 py-1 border border-border rounded-md text-sm bg-background">

              <option value="all">All Updates</option>
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="alerts">Alerts</option>
            </select>
            <button className="p-2 hover:bg-muted rounded-md transition-colors" title="Refresh">
              <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
        {filteredUpdates?.length === 0 ?
        <div className="text-center py-8">
            <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No updates available for the selected filter.</p>
          </div> :

        filteredUpdates?.map((update) => {
          const statusInfo = getStatusIcon(update?.status);
          return (
            <div key={update?.id} className={`border-l-4 ${getPriorityColor(update?.priority)} bg-muted/20 rounded-r-lg p-4 hover:bg-muted/30 transition-colors`}>
                <div className="flex items-start space-x-4">
                  {/* Avatar & Status */}
                  <div className="relative flex-shrink-0">
                    <Image
                    src={update?.avatar}
                    alt={update?.avatarAlt}
                    className="w-10 h-10 rounded-full object-cover" />

                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-card ${statusInfo?.color} bg-card flex items-center justify-center`}>
                      <Icon name={statusInfo?.icon} size={12} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-foreground">{update?.member}</h4>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">{formatTimeAgo(update?.timestamp)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {update?.gpsVerified &&
                      <div className="flex items-center space-x-1 text-xs text-success">
                            <Icon name="MapPin" size={12} />
                            <span>GPS</span>
                          </div>
                      }
                        {update?.photos > 0 &&
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Icon name="Camera" size={12} />
                            <span>{update?.photos}</span>
                          </div>
                      }
                      </div>
                    </div>

                    <h5 className="text-sm font-medium text-foreground mb-1">{update?.task}</h5>
                    <p className="text-xs text-muted-foreground mb-2">{update?.location?.name}</p>
                    <p className="text-sm text-foreground">{update?.details}</p>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 mt-3">
                      <button className="px-3 py-1 bg-primary text-primary-foreground rounded text-xs hover:bg-primary/90 transition-colors">
                        View Details
                      </button>
                      <button className="px-3 py-1 border border-border rounded text-xs hover:bg-muted transition-colors">
                        View Location
                      </button>
                      {update?.status === 'alert' &&
                    <button className="px-3 py-1 bg-error text-error-foreground rounded text-xs hover:bg-error/90 transition-colors">
                          Respond
                        </button>
                    }
                    </div>
                  </div>
                </div>
                {/* GPS Map Preview for alerts */}
                {update?.status === 'alert' &&
              <div className="mt-4 h-32 rounded-lg overflow-hidden border border-border">
                    <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={update?.location?.name}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${update?.location?.lat},${update?.location?.lng}&z=16&output=embed`}
                  className="border-0" />

                  </div>
              }
              </div>);

        })
        }
      </div>
      {/* Summary Stats */}
      <div className="px-6 py-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-lg font-bold text-success">{updates?.filter((u) => u?.status === 'completed')?.length}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-warning">{updates?.filter((u) => u?.status === 'in-progress')?.length}</p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-error">{updates?.filter((u) => u?.status === 'alert' || u?.status === 'delayed')?.length}</p>
            <p className="text-xs text-muted-foreground">Alerts</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-primary">{updates?.filter((u) => u?.gpsVerified)?.length}</p>
            <p className="text-xs text-muted-foreground">GPS Verified</p>
          </div>
        </div>
      </div>
    </div>);

};

export default RealTimeTaskUpdates;